// Phase 17.1 — Top-level error boundary around the app shell.
// Catches render-time errors anywhere in the Routes subtree and renders a
// branded fallback card so the entire app never shows a blank screen.
import React from "react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: (error && (error.message || String(error))) || "Unknown error",
    };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught:", error, info?.componentStack);
  }

  handleReload = () => {
    try { window.location.reload(); } catch (_e) { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = (this.state.errorMessage || "").slice(0, 300);
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ background: "#f6efe0" }}
        data-testid="error-boundary-fallback"
      >
        <div
          className="w-full max-w-lg rounded-lg bg-white border-t-4 shadow-lg p-6"
          style={{ borderTopColor: "#7a1f2b" }}
        >
          <div className="flex items-center gap-3 mb-3">
            <img
              src="/vgws-logo.png"
              alt="VGWS"
              width="40" height="40"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div>
              <div
                className="font-serif text-xl font-semibold"
                style={{ color: "#7a1f2b" }}
                data-testid="error-boundary-title"
              >
                Something went wrong
              </div>
              <div className="text-xs" style={{ color: "#6e7770" }}>
                VGWS Requisition Portal
              </div>
            </div>
          </div>
          <p className="text-sm mb-3" style={{ color: "#4a524c" }}>
            An unexpected error occurred while rendering this page. Please
            reload — if the issue persists, contact your administrator.
          </p>
          {msg && (
            <pre
              className="text-xs rounded px-3 py-2 mb-4 overflow-auto"
              style={{
                background: "#faf5eb",
                border: "1px solid #e2d7c3",
                color: "#1f2320",
                maxHeight: 160,
              }}
              data-testid="error-boundary-message"
            >
              {msg}
            </pre>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            className="w-full py-2 rounded-md font-medium text-white"
            style={{ background: "#7a1f2b" }}
            data-testid="error-boundary-reload"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
